import { z } from 'zod'
import { Event } from '../common'
import { bzOptional } from '../domain/common-schemas'
import { guidSchema, isoDateStringSchema, localDateSchema } from './_common'
import { PriceEngineOptionsSchema } from './PriceEngineOptions'

export const visitRequestSchema = z.object({
  visitGuid: guidSchema,

  name: z.string(),
  affinityDate: localDateSchema,
  jobTypeGuid: bzOptional(guidSchema),
  completedAtOverride: bzOptional(isoDateStringSchema),
  isCompletedOverride: bzOptional(z.boolean()),
  shouldLinkToJob: bzOptional(z.boolean()),
  jobGuid: bzOptional(guidSchema),
  visitNote: bzOptional(z.string()),

  installedEquipmentGuids: z.array(guidSchema),
})

export type MaintenancePlanVisitRequest = z.infer<typeof visitRequestSchema>

export const maintenancePlanTaxRateSchema = z.object({
  taxRateGuid: guidSchema,
  rate: z.number(),
})

export const maintenancePlanCreatedSchema = z.object({
  accountGuid: guidSchema,
  locationGuid: guidSchema,
  businessUnitGuid: bzOptional(guidSchema),
  taxRate: bzOptional(maintenancePlanTaxRateSchema),
  soldByUserGuid: bzOptional(guidSchema),
})

export const MaintenancePlanCreatedEventName = 'MaintenancePlanCreated' as const
export type MaintenancePlanCreatedEventData = z.infer<typeof maintenancePlanCreatedSchema>
export type MaintenancePlanCreatedEvent = Event<MaintenancePlanCreatedEventData>

export const maintenancePlanDiscountBenefitItemSchema = z.object({
  jobClass: z.string(),
  discountRate: z.number(),
})

export type MaintenancePlanDiscountBenefitItem = z.infer<typeof maintenancePlanDiscountBenefitItemSchema>

export const maintenancePlanCoveredEquipmentItemSchema = z.object({
  installedEquipmentGuid: guidSchema,
  equipmentType: z.string(),
})

export const maintenancePlanConfigurationSchema = z.object({
  maintenancePlanDefinitionGuid: guidSchema,
  numVisitsPerYear: z.number(),
  numDaysUntilVisitCreditExpiration: z.number(),
  coveredEquipment: z.array(maintenancePlanCoveredEquipmentItemSchema),
  discounts: z.array(maintenancePlanDiscountBenefitItemSchema),
  scheduledActivationAt: bzOptional(isoDateStringSchema),

  suppressActivationEmail: bzOptional(z.boolean()),
  initialVisits: bzOptional(z.array(visitRequestSchema)),

  yearlyStaticPriceUsc: bzOptional(z.number()),
  numFixedDurationYears: bzOptional(z.number()),
})

export const MaintenancePlanConfiguredEventName = 'MaintenancePlanConfigured' as const
export type MaintenancePlanConfiguredEventData = z.infer<typeof maintenancePlanConfigurationSchema>
export type MaintenancePlanConfiguredEvent = Event<MaintenancePlanConfiguredEventData>

export const maintenancePlanVisitMonthsChangedSchema = z.object({
  changes: z.array(z.object({ visitGuid: guidSchema, affinityDate: localDateSchema })),
})
export const MaintenancePlanVisitMonthsChangedEventName = 'MaintenancePlanVisitMonthsChanged' as const
export type MaintenancePlanVisitMonthsChangedEventData = z.infer<typeof maintenancePlanVisitMonthsChangedSchema>

export const maintenancePlanActivationSchema = z.object({
  paymentSubscriptionGuid: guidSchema,
  customBillingStartAt: bzOptional(isoDateStringSchema),
  numDaysUntilAutoCancelation: bzOptional(z.number()),
})

export const MaintenancePlanActivatedEventName = 'MaintenancePlanActivated' as const
export type MaintenancePlanActivatedEventData = z.infer<typeof maintenancePlanActivationSchema>
export type MaintenancePlanActivatedEvent = Event<MaintenancePlanActivatedEventData>

export const freeMaintenancePlanActivationSchema = z.object({
  numDaysUntilAutoCancelation: bzOptional(z.number()),
})

export const FreeMaintenancePlanActivatedEventName = 'FreeMaintenancePlanActivated' as const
export type FreeMaintenancePlanActivatedEventData = z.infer<typeof freeMaintenancePlanActivationSchema>
export type FreeMaintenancePlanActivatedEvent = Event<FreeMaintenancePlanActivatedEventData>

export const importedMaintenancePlanActivationSchema = z.object({
  activatedAt: isoDateStringSchema,
  numDaysUntilAutoCancelation: z.number(),
})

export const ImportedMaintenancePlanActivatedEventName = 'ImportedMaintenancePlanActivated' as const
export type ImportedMaintenancePlanActivatedEventData = z.infer<typeof importedMaintenancePlanActivationSchema>
export type ImportedMaintenancePlanActivatedEvent = Event<ImportedMaintenancePlanActivatedEventData>

export const MaintenancePlanFixedDurationSetEventName = 'MaintenancePlanSetAsFixedDuration' as const
export type MaintenancePlanFixedDurationSetEvent = Event

export const maintenancePlanActivatedWithOneTimePaymentSchema = z.object({
  paymentRecordGuid: guidSchema,
  paymentAmountUsc: z.number(),
  numDaysUntilAutoCancelation: z.number(),
  scheduledActivationAt: bzOptional(isoDateStringSchema),
  // Coverage-anchor date: activation and the fixed-term clock run from here. Defaults to the event's
  // occurredAt (now) when omitted; the BZ-450 backfill supplies the real payment date so a long-stuck
  // plan's 1-year term reflects when the customer actually paid, not the backfill run date.
  activatedAt: bzOptional(isoDateStringSchema),
})

export const MaintenancePlanActivatedWithOneTimePaymentEventName = 'MaintenancePlanActivatedWithOneTimePayment' as const
export type MaintenancePlanActivatedWithOneTimePaymentEventData = z.infer<
  typeof maintenancePlanActivatedWithOneTimePaymentSchema
>
export type MaintenancePlanActivatedWithOneTimePaymentEvent = Event<MaintenancePlanActivatedWithOneTimePaymentEventData>

// Activation of a MANUAL plan whose linked invoice was paid in full WITHOUT a payment record
// (e.g. a fully-discounted / $0 invoice that auto-marks PAID). Mirrors activation-with-one-time-payment
// but records no payment. Activation is driven off the invoice->plan link, so a plan no longer depends
// on a payment carrying its maintenance_plan_guid to leave PENDING. See BZ-450.
export const maintenancePlanActivatedWithoutPaymentSchema = z.object({
  numDaysUntilAutoCancelation: z.number(),
  // The paid invoice that triggered activation. Audit/traceability only — idempotency is enforced by the
  // PENDING status guard at the application layer, not by this field.
  invoiceGuid: bzOptional(guidSchema),
  // Coverage-anchor date (see maintenancePlanActivatedWithOneTimePaymentSchema). Defaults to occurredAt
  // (now); the backfill supplies the invoice's issued date for a long-stuck $0 plan.
  activatedAt: bzOptional(isoDateStringSchema),
})

export const MaintenancePlanActivatedWithoutPaymentEventName = 'MaintenancePlanActivatedWithoutPayment' as const
export type MaintenancePlanActivatedWithoutPaymentEventData = z.infer<
  typeof maintenancePlanActivatedWithoutPaymentSchema
>
export type MaintenancePlanActivatedWithoutPaymentEvent = Event<MaintenancePlanActivatedWithoutPaymentEventData>

export const MaintenancePlanScheduledEventName = 'MaintenancePlanScheduled' as const

export const maintenancePlanScheduledSchema = z.object({
  scheduledActivationAt: isoDateStringSchema,
  numDaysUntilAutoCancelation: bzOptional(z.number()),
  payment: bzOptional(
    z.object({
      paymentRecordGuid: guidSchema,
      paymentAmountUsc: z.number(),
    }),
  ),
})

export type MaintenancePlanScheduledEventData = z.infer<typeof maintenancePlanScheduledSchema>
export type MaintenancePlanScheduledEvent = Event<MaintenancePlanScheduledEventData>

export const MaintenancePlanScheduledActivationEventName = 'MaintenancePlanScheduledActivation' as const

export const maintenancePlanScheduledActivationSchema = z.object({
  scheduledActivationAt: isoDateStringSchema,
})

export type MaintenancePlanScheduledActivationEventData = z.infer<typeof maintenancePlanScheduledActivationSchema>
export type MaintenancePlanScheduledActivationEvent = Event<MaintenancePlanScheduledActivationEventData>

/**
 * Every event that transitions a maintenance plan to ACTIVE. Reactive hooks that must fire when a plan
 * becomes active (initial visit creation, activation email, activity-feed / business-event projection)
 * derive their event lists from this single source so a newly-added activation event cannot silently drop
 * out of one of them — the incomplete-enumeration bug that BZ-450's $0 path nearly shipped.
 */
export const MAINTENANCE_PLAN_ACTIVATION_EVENT_NAMES: readonly string[] = [
  MaintenancePlanActivatedEventName,
  FreeMaintenancePlanActivatedEventName,
  ImportedMaintenancePlanActivatedEventName,
  MaintenancePlanActivatedWithOneTimePaymentEventName,
  MaintenancePlanActivatedWithoutPaymentEventName,
  MaintenancePlanScheduledActivationEventName,
]

export const MaintenancePlanRescheduledEventName = 'MaintenancePlanRescheduled' as const

export const maintenancePlanRescheduledSchema = z.object({
  scheduledActivationAt: isoDateStringSchema,
  updatedInitialVisits: bzOptional(z.array(visitRequestSchema)),
})

export type MaintenancePlanRescheduledEventData = z.infer<typeof maintenancePlanRescheduledSchema>
export type MaintenancePlanRescheduledEvent = Event<MaintenancePlanRescheduledEventData>

export enum MaintenancePlanCancellationReasonType {
  FINANCIAL = 'FINANCIAL',
  SERVICE_QUALITY = 'SERVICE_QUALITY',
  SERVICE_REQUIREMENTS = 'SERVICE_REQUIREMENTS',
  CONTRACT_ISSUE = 'CONTRACT_ISSUE',
  LOCATION_CHANGE = 'LOCATION_CHANGE',
  COMPETITOR_OFFER = 'COMPETITOR_OFFER',
  LIFESTYLE_CHANGE = 'LIFESTYLE_CHANGE',
  ENVIRONMENT_HEALTH_CONCERNS = 'ENVIRONMENT_HEALTH_CONCERNS',
  PLAN_USAGE = 'PLAN_USAGE',
  NEVER_ACTIVATED = 'NEVER_ACTIVATED',
  OTHER = 'OTHER',
}

export const maintenancePlanCancellationReasonTypesDisplayNames = {
  [MaintenancePlanCancellationReasonType.FINANCIAL]: 'Financial',
  [MaintenancePlanCancellationReasonType.SERVICE_QUALITY]: 'Service Quality',
  [MaintenancePlanCancellationReasonType.SERVICE_REQUIREMENTS]: 'Service Requirements',
  [MaintenancePlanCancellationReasonType.CONTRACT_ISSUE]: 'Contract Issue',
  [MaintenancePlanCancellationReasonType.LOCATION_CHANGE]: 'Location Change',
  [MaintenancePlanCancellationReasonType.COMPETITOR_OFFER]: 'Competitor Offer',
  [MaintenancePlanCancellationReasonType.LIFESTYLE_CHANGE]: 'Lifestyle Change',
  [MaintenancePlanCancellationReasonType.ENVIRONMENT_HEALTH_CONCERNS]: 'Environment & Health Concerns',
  [MaintenancePlanCancellationReasonType.PLAN_USAGE]: 'Plan Usage',
  [MaintenancePlanCancellationReasonType.NEVER_ACTIVATED]: 'Never Activated',
  [MaintenancePlanCancellationReasonType.OTHER]: 'Other',
}

export const maintenancePlanCanceledSchema = z.object({
  cancellationReason: bzOptional(z.string()),
  cancellationReasonType: bzOptional(z.nativeEnum(MaintenancePlanCancellationReasonType)),
  suppressCancellationEmail: bzOptional(z.boolean()),
  shouldExpireVisitsImmediately: bzOptional(z.boolean()),
})

export const MaintenancePlanCanceledEventName = 'MaintenancePlanCanceled' as const
export type MaintenancePlanCanceledEventData = z.infer<typeof maintenancePlanCanceledSchema>
export type MaintenancePlanCanceledEvent = Event<MaintenancePlanCanceledEventData>

export const MaintenancePlanLapsedEventName = 'MaintenancePlanLapsed' as const
export type MaintenancePlanLapsedEvent = Event

export const MaintenancePlanExpiredEventName = 'MaintenancePlanExpired' as const
export type MaintenancePlanExpiredEvent = Event

export const maintenancePlanLifecycleCorrectedSchema = z.object({
  executionId: guidSchema,
  manifestSha256: z.string().regex(/^[a-f0-9]{64}$/),
  reason: z.string().min(1),
  timeZone: z.string().min(1),
  termEndInclusivity: z.literal('EXCLUSIVE'),
  activatedAt: isoDateStringSchema,
  terminatesAt: isoDateStringSchema,
  visitCorrections: z.array(
    z.object({
      sourceVisitGuid: guidSchema,
      targetVisitGuid: guidSchema,
      affinityDate: localDateSchema,
    }),
  ),
})

export const MaintenancePlanLifecycleCorrectedEventName = 'MaintenancePlanLifecycleCorrected' as const
export type MaintenancePlanLifecycleCorrectedEventData = z.infer<typeof maintenancePlanLifecycleCorrectedSchema>
export type MaintenancePlanLifecycleCorrectedEvent = Event<MaintenancePlanLifecycleCorrectedEventData>

export enum MaintenancePlanPaymentInterval {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
  // This means the interval isn't applicable. The payment is fully collected upfront.
  UPFRONT = 'UPFRONT',
}

export const maintenancePlanVisitConfigSchema = z.object({
  displayName: z.string(),
  affinityMonth: z.string(),
  jobTypeGuid: bzOptional(guidSchema),
})

export type MaintenancePlanVisitConfig = z.infer<typeof maintenancePlanVisitConfigSchema>

export type MaintenancePlanVisitConfigArray = MaintenancePlanVisitConfig[]

export const NumPeriodsPerYearForInterval = {
  [MaintenancePlanPaymentInterval.YEARLY]: 1,
  [MaintenancePlanPaymentInterval.MONTHLY]: 12,
  [MaintenancePlanPaymentInterval.QUARTERLY]: 4,
  [MaintenancePlanPaymentInterval.UPFRONT]: 1,
}

export const maintenancePlanPricingSchema = z.object({
  subtotalPeriodPriceUsc: z.number(),
  discountPeriodAmountUsc: z.number(),
  paymentInterval: z.nativeEnum(MaintenancePlanPaymentInterval),
  // NOTE: Must be optional to be backward compatible with old Domain Events
  upchargePeriodAmountUsc: bzOptional(z.number()),
  priceEngineOptions: PriceEngineOptionsSchema,
})

export const MaintenancePlanPricingSetEventName = 'MaintenancePlanPricingSet' as const
export type MaintenancePlanPricingSetEventData = z.infer<typeof maintenancePlanPricingSchema>
export type MaintenancePlanPricingSetEvent = Event<MaintenancePlanPricingSetEventData>

export const maintenancePlanPaymentReceivedSchema = z.object({
  paymentRecordGuid: guidSchema,
  paymentAmountUsc: z.number(),
  isOneTimePayment: bzOptional(z.boolean()),
})

export const MaintenancePlanPaymentReceivedEventName = 'MaintenancePlanPaymentReceived' as const
export type MaintenancePlanPaymentReceivedEventData = z.infer<typeof maintenancePlanPaymentReceivedSchema>
export type MaintenancePlanPaymentReceivedEvent = Event<MaintenancePlanPaymentReceivedEventData>

export const maintenancePlanReplenishmentAnchorSetSchema = z.union([
  z.object({
    clear: z.literal(true),
  }),
  z.object({
    replenishmentMonth: z.number().int().min(1).max(12),
    replenishmentDay: z.number().int().min(1).max(28),
  }),
])

export const MaintenancePlanReplenishmentAnchorSetEventName = 'MaintenancePlanReplenishmentAnchorSet' as const
export type MaintenancePlanReplenishmentAnchorSetEventData = z.infer<typeof maintenancePlanReplenishmentAnchorSetSchema>
export type MaintenancePlanReplenishmentAnchorSetEvent = Event<MaintenancePlanReplenishmentAnchorSetEventData>

export const maintenancePlanVisitCompletedSchema = z.object({
  invoiceGuid: bzOptional(guidSchema),
  jobGuid: bzOptional(guidSchema),
})

export const MaintenancePlanVisitCompletedEventName = 'MaintenancePlanVisitCompleted' as const
export type MaintenancePlanVisitCompletedEventData = z.infer<typeof maintenancePlanVisitCompletedSchema>
export type MaintenancePlanVisitCompletedEvent = Event<MaintenancePlanVisitCompletedEventData>

export const maintenancePlanHistoricalPaymentReceivedSchema = z.object({
  paymentRecordGuid: bzOptional(guidSchema),
  paymentReceivedAt: isoDateStringSchema,
  paymentAmountUsc: z.number(),
})
export type MaintenancePlanHistoricalPaymentReceived = z.infer<typeof maintenancePlanHistoricalPaymentReceivedSchema>

export const maintenancePlanHistoricalVisitsSchema = z.object({
  visitAt: isoDateStringSchema,
  jobGuid: bzOptional(guidSchema),
  invoiceGuid: bzOptional(guidSchema),
})
export type MaintenancePlanHistoricalVisits = z.infer<typeof maintenancePlanHistoricalVisitsSchema>

export const maintenancePlanHistoryImportedEventData = z.object({
  activatedAt: isoDateStringSchema,
  numDaysUntilAutoCancelation: bzOptional(z.number().int()),
  historicalPaymentsReceived: z.array(maintenancePlanHistoricalPaymentReceivedSchema).min(1),
  historicalVisits: bzOptional(z.array(maintenancePlanHistoricalVisitsSchema)),
})

export const MaintenancePlanHistoryImportedEventName = 'MaintenancePlanHistoryImported' as const
export type MaintenancePlanHistoryImportedEventData = z.infer<typeof maintenancePlanHistoryImportedEventData>
export type MaintenancePlanHistoryImportedEvent = Event<MaintenancePlanHistoryImportedEventData>

export const FreeMaintenancePlanCreditsGrantedEventName = 'FreeMaintenancePlanCreditsGranted' as const
export type FreeMaintenancePlanCreditsGrantedEvent = Event<null>

export const maintenancePlanMigratedCreditsToVisitsData = z.object({
  migrations: z.array(z.object({ creditIndex: z.number(), visitGuid: guidSchema })),
})

export const MaintenancePlanMigratedCreditsToVisitsEventName = 'MaintenancePlanMigratedCreditsToVisits' as const
export type MaintenancePlanMigratedCreditsToVisitsEventData = z.infer<typeof maintenancePlanMigratedCreditsToVisitsData>
export type MaintenancePlanMigratedCreditsToVisitsEvent = Event<MaintenancePlanMigratedCreditsToVisitsEventData>

export const MaintenancePlanMigratedToStaticPricingEventName = 'MaintenancePlanMigratedToStaticPricing' as const
export const maintenancePlanMigratedToStaticPricingSchema = z.object({
  yearlyStaticPriceUsc: z.number(),
})
export type MaintenancePlanMigratedToStaticPricingEventData = z.infer<
  typeof maintenancePlanMigratedToStaticPricingSchema
>
export type MaintenancePlanMigratedToStaticPricingEvent = Event<MaintenancePlanMigratedToStaticPricingEventData>

export const maintenancePlanBusinessUnitSetSchema = z.object({
  businessUnitGuid: bzOptional(guidSchema),
})

export const MaintenancePlanBusinessUnitSetEventName = 'MaintenancePlanBusinessUnitSet' as const
export type MaintenancePlanBusinessUnitSetEventData = z.infer<typeof maintenancePlanBusinessUnitSetSchema>
export type MaintenancePlanBusinessUnitSetEvent = Event<MaintenancePlanBusinessUnitSetEventData>

export const maintenancePlanSetSoldBySchema = z.object({
  soldByUserGuid: guidSchema,
})
export const MaintenancePlanSetSoldByEventName = 'MaintenancePlanSetSoldBy' as const
export type MaintenancePlanSetSoldByEventData = z.infer<typeof maintenancePlanSetSoldBySchema>
export type MaintenancePlanSetSoldByEvent = Event<MaintenancePlanSetSoldByEventData>
